import React, { useState, useEffect, useMemo } from 'react';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { NavLink, Outlet, useMatch } from 'react-router-dom';
import {
  Plus, Phone, Users, ChevronRight, Mail, Search, X,
} from 'lucide-react';
import { customersCol } from './database.js';
import CustomerWizard from './CustomerWizard.jsx';
import ArchivedCustomers from './ArchivedCustomers.jsx';
import { activeCustomers, archivedCustomers } from './lib/job-helpers.js';

// Strip non-digits, format as (XXX) XXX-XXXX for US numbers (else return as-is).
function formatPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw || '';
}

function formatCustomerSince(ts) {
  if (!ts?.toDate) return null;
  return ts.toDate().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function Customers() {
  const [allCustomers, setAllCustomers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [modalState, setModalState] = useState(null);
  const [search, setSearch] = useState('');

  // Detect whether we're at /customers/:customerId. On phone this hides
  // the list; on tablet+ it lights up the selected card and shows Jobs
  // in the right pane.
  const detailMatch = useMatch('/customers/:customerId');
  const hasDetail = !!detailMatch;

  // The list, the count and the empty state are all about ACTIVE
  // customers. Archived ones live in their own panel at the bottom.
  const customers = useMemo(() => activeCustomers(allCustomers), [allCustomers]);
  const archived  = useMemo(() => archivedCustomers(allCustomers), [allCustomers]);

  const filteredCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    const qDigits = search.replace(/\D/g, '');
    return customers.filter((c) => {
      if (c.name?.toLowerCase().includes(q)) return true;
      if (c.email?.toLowerCase().includes(q)) return true;
      if (qDigits && String(c.phone || '').replace(/\D/g, '').includes(qDigits)) return true;
      return false;
    });
  }, [customers, search]);

  useEffect(() => {
    const q = query(customersCol(), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAllCustomers(snap.docs.map((d) => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) })));
        setLoaded(true);
      },
      (err) => {
        console.error('Customers load failed:', err);
        setLoaded(true);
      }
    );
    return unsub;
  }, []);

  // "+ Add Customer" opens the 3-step CustomerWizard — same flow as the
  // Home + NEW button. modalState is a bool now (open/closed), no form
  // payload needed since the wizard owns its own state.
  function openAdd() { setModalState(true); }
  function closeModal() { setModalState(null); }
  // Edit + Delete actions live on the customer detail page (next to the
  // cog wheel) — the list card stays purely informational.

  return (
    <div className="customers-screen">
      <div className="screen-header">
        <div>
          <h1>Customers</h1>
          <p className="subtitle">Everyone you've ever worked for, in one list.</p>
        </div>
        <button
          className="btn"
          onClick={openAdd}
        >
          <Plus size={16} strokeWidth={2.5} />
          Add Customer
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">
            <Users size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Total customers
          </div>
          <div className="stat-value">{loaded ? customers.length : '—'}</div>
        </div>
      </div>

      {!loaded && <p className="muted small">Loading customers…</p>}

      {loaded && customers.length === 0 && (
        <div className="empty-state">
          <Users size={32} color="var(--text-3)" strokeWidth={1.5} style={{ marginBottom: 8 }} />
          <p style={{ fontWeight: 600 }}>
            {archived.length > 0 ? 'No active customers.' : 'No customers yet.'}
          </p>
          <p className="muted small">
            {archived.length > 0
              ? <>Everyone you have is archived. Restore one below, or tap <strong>+ Add Customer</strong>.</>
              : <>Tap <strong>+ Add Customer</strong> to add your first one.</>}
          </p>
        </div>
      )}

      {loaded && customers.length > 0 && (
        <div className={`customers-shell ${hasDetail ? 'has-detail' : ''}`}>
          <div className="customers-list-pane">
            <div className="search-row">
              <Search size={14} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone, or email"
              />
              {search && (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {filteredCustomers.length === 0 ? (
              <p className="muted small empty-inline">
                No customers match "<strong>{search}</strong>".
              </p>
            ) : (
              <div className="list-narrow">
                {filteredCustomers.map((c) => (
                  <CustomerCard key={c.id} customer={c} />
                ))}
              </div>
            )}
          </div>

          <div className="customers-detail-pane">
            {hasDetail ? (
              <Outlet />
            ) : (
              <div className="customers-empty-detail">
                <Users size={28} color="var(--text-3)" strokeWidth={1.5} />
                <p>Select a customer from the list to see their vehicles + jobs.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {loaded && <ArchivedCustomers customers={archived} />}

      {modalState && <CustomerWizard onClose={closeModal} />}
    </div>
  );
}

function CustomerCard({ customer }) {
  const since = formatCustomerSince(customer.createdAt);
  return (
    <article className="card customer-card">
      <NavLink
        to={`/customers/${customer.id}`}
        className={({ isActive }) => `card-body ${isActive ? 'active' : ''}`}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="card-title">{customer.name}</div>
            {customer.phone && (
              <div className="card-sub mono" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                <Phone size={12} />
                {formatPhone(customer.phone)}
              </div>
            )}
            {customer.email && (
              <div className="card-sub" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, wordBreak: 'break-all' }}>
                <Mail size={12} />
                {customer.email}
              </div>
            )}
            {since && (
              <div className="muted-2 small" style={{ marginTop: 6, fontSize: 11 }}>
                Customer since {since}
              </div>
            )}
          </div>
          <ChevronRight size={18} color="var(--text-3)" style={{ flexShrink: 0, marginTop: 4 }} />
        </div>
      </NavLink>
    </article>
  );
}
